import React from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, CircleNotch, MagnifyingGlass, CaretDown } from "@phosphor-icons/react";
import { Card, Input, Btn } from "../epbcs/common";
import { ReportRunner, SqlViewer } from "./ReportRunner";

export default function QueriesSection() {
  const [scan, setScan] = React.useState(null);
  const [root, setRoot] = React.useState("/Custom");
  const [scanning, setScanning] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [tab, setTab] = React.useState("reports");
  const [expanded, setExpanded] = React.useState(null);

  React.useEffect(() => {
    api.get("/reporting/scan").then((r) => {
      setScan(r.data);
      if (r.data.root) setRoot(r.data.root);
    }).catch(() => setScan({ reports: [], data_models: [] }));
  }, []);

  const runScan = async () => {
    setScanning(true); setExpanded(null);
    try {
      const r = await api.post("/reporting/scan", { root });
      setScan(r.data);
      toast.success(`Scanned ${r.data.folders_scanned} folders — ${r.data.reports.length} reports, ${r.data.data_models.length} data models`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Catalog scan failed");
    } finally { setScanning(false); }
  };

  const rows = (tab === "reports" ? scan?.reports : scan?.data_models) || [];
  const filtered = rows.filter((r) => !q ||
    r.name.toLowerCase().includes(q.toLowerCase()) || r.path.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <Card
        title="Full Catalog Scan"
        subtitle="Walk the entire BI Publisher catalog from a root folder and index every report and data model query in one pass."
        testid="queries-scan-card"
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-72">
            <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500 mb-1">Root folder</div>
            <Input data-testid="scan-root-input" value={root} onChange={(e) => setRoot(e.target.value)} placeholder="/Custom" />
          </div>
          <Btn data-testid="scan-run-btn" onClick={runScan} disabled={scanning}>
            <span className="flex items-center gap-2">
              {scanning ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
              {scanning ? "Scanning catalog…" : "Scan Catalog"}
            </span>
          </Btn>
          {scan?.scanned_at && (
            <span className="text-[11px] text-slate-400 pb-2" data-testid="scan-meta">
              Last scan: {new Date(scan.scanned_at).toLocaleString()} · {scan.folders_scanned} folders
              {scan.truncated ? " · truncated (raise folder limit)" : ""}
            </span>
          )}
        </div>
      </Card>

      <Card testid="queries-results-card">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex border border-slate-200">
            {[["reports", `Reports (${scan?.reports?.length ?? 0})`],
              ["data_models", `Data Models (${scan?.data_models?.length ?? 0})`]].map(([k, l]) => (
              <button key={k} data-testid={`queries-tab-${k}`}
                onClick={() => { setTab(k); setExpanded(null); }}
                className={`px-4 py-1.5 text-[12px] font-semibold tracking-wide transition-colors ${
                  tab === k ? "bg-[#0B1E3F] text-white" : "text-slate-600 hover:bg-slate-50"
                }`}>
                {l}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="queries-search-input" className="pl-8" placeholder="Search name or path…"
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>

        {scan === null && (
          <div className="text-[12px] text-slate-500 py-6 text-center">Loading…</div>
        )}
        {scan !== null && filtered.length === 0 && (
          <div className="text-[12px] text-slate-400 py-8 text-center" data-testid="queries-empty">
            {rows.length === 0 ? "No scan results yet — run a catalog scan above." : "No matches for your search."}
          </div>
        )}
        <div className="divide-y divide-slate-100">
          {filtered.map((r) => {
            const isOpen = expanded === r.path;
            return (
              <div key={r.path}>
                <button
                  data-testid={`queries-row-${r.name.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setExpanded(isOpen ? null : r.path)}
                  className={`w-full flex items-center gap-3 px-2 py-2.5 text-left transition-colors border-l-2 ${
                    isOpen ? "border-[#C8A24B] bg-[#0B1E3F]/[0.04]" : "border-transparent hover:bg-slate-50"
                  }`}>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-medium text-[#0F172A] truncate">{r.name}</span>
                    <span className="block text-[10px] text-slate-400 font-mono truncate">{r.path}</span>
                  </span>
                  <span className="text-[10px] text-slate-500 hidden sm:block">{r.folder}</span>
                  <CaretDown size={12} className={`text-slate-400 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                </button>
                {isOpen && (
                  <div className="px-4 py-4 bg-slate-50/60 border-l-2 border-[#C8A24B]">
                    {tab === "reports"
                      ? <ReportRunner path={r.path} name={r.name} />
                      : <SqlViewer path={r.path} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
